import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * SmartMoneyTrap — Variance vs. Count Divergence
 *
 * How it works:
 * - If `Final OI % Variance` is strongly Bullish (> +20%), but the `Total Count` is deeply Bearish (≤ -10),
 *   it means retail is buying calls while smart money is shifting strikes lower. 
 * - Buys ATM PE to ride the crash. (And vice-versa for CE).
 */
export const evaluateSmartMoneyTrap: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, activeRange, baseSnapshot } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    if (fullTrendData.length < 1) return null;

    const latestRow = fullTrendData[0];
    const totalCount = latestRow?.totalCount ?? 0;

    // Calculate Final OI % Variance independently
    let minDiff = Infinity;
    let maxDiff = -Infinity;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    
    const baseRawOptsArr = extractSnapshotOptions(baseSnapshot, symbol, expiry) || [];
    const baseFlatOpts = ensureFlatOpts(baseRawOptsArr);

    if (!liveFlatOpts.length || !baseFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const minStrike = atmStrike - (activeRange * gap);
    const maxStrike = atmStrike + (activeRange * gap);

    for (let currentStrike = minStrike; currentStrike <= maxStrike; currentStrike += gap) {
        const liveCE = liveFlatOpts.find((o: any) => o.strike === currentStrike && o.type === 'CE');
        const livePE = liveFlatOpts.find((o: any) => o.strike === currentStrike && o.type === 'PE');
        const baseCE = baseFlatOpts.find((o: any) => o.strike === currentStrike && o.type === 'CE');
        const basePE = baseFlatOpts.find((o: any) => o.strike === currentStrike && o.type === 'PE');

        if (liveCE && livePE && baseCE && basePE) {
            const liveCeOi = liveCE.oi || 1;
            const baseCeOi = baseCE.oi || 1;
            const ceOiDiff = ((baseCeOi - liveCeOi) / liveCeOi) * 100;

            const livePeOi = livePE.oi || 1;
            const basePeOi = basePE.oi || 1;
            const peOiDiff = ((basePeOi - livePeOi) / livePeOi) * 100;

            const variance = peOiDiff - ceOiDiff;

            if (variance < minDiff) minDiff = variance;
            if (variance > maxDiff) maxDiff = variance;
        }
    }

    let finalOiVariance = 0;
    if (minDiff !== Infinity && maxDiff !== -Infinity) {
        if (minDiff > 0) {
            finalOiVariance = maxDiff - minDiff;
        } else {
            finalOiVariance = maxDiff + minDiff;
        }
    }

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // Trap: Variance Bullish (> +20%), but Total Count Bearish (<= -10)
    if (finalOiVariance > 20 && totalCount <= -10) {
        status = 'BEAR';
        remarks = `Bull Trap: Variance (+${finalOiVariance.toFixed(0)}%) vs Count (${totalCount})`;
    }
    // Trap: Variance Bearish (< -20%), but Total Count Bullish (>= +10)
    else if (finalOiVariance < -20 && totalCount >= 10) {
        status = 'BULL';
        remarks = `Bear Trap: Variance (${finalOiVariance.toFixed(0)}%) vs Count (+${totalCount})`;
    }

    if (status === 'Sideways') return null;

    const optionType = status === 'BULL' ? 'CE' : 'PE';

    let entryPrice = 0;
    const atmRow = liveFlatOpts.find((r: any) => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
